import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FileDown, Send, CheckCircle2 } from "lucide-react";
import { generatePrescriptionHTML } from "@/lib/prescriptionPdf";
import {
  getNhs111EmergencySupplyFormData,
  formatFormDataAsTemplate,
} from "./UseNhs111EmergencySupplyFormData";
import { getPatientByPatientId } from "@/dispatchers/patientDispatchers";
import { usePrescriptionData } from "@/lib/prescriptionData";
import { useState } from "react";

interface NHS111SummaryData {
  patientConsent: string;
  consultationDate: string;
  consultationTime: string;
  consultationMethod: string;
  patientAccompanied: string;
  accompaniedRelationship: string;
  accompaniedRelationshipOther: string;
  reasonForRequest: string;
  reasonForRequestOther: string;
  ncrsRecordAccessed: string;
  electronicPrescriptionAvailable: string;
  outcomes: string;
  outcomesOther: string;
  referralDestination: string;
  referralDestinationOther: string;
  escalationDestination: string;
  escalationDestinationOther: string;
  onwardReferralReason: string;
  onwardReferralDate: string;
  onwardReferralTime: string;
  destinationOdsCode: string;
  adviceGiven: boolean;
  adviceProvidedTo: string;
  planAndRequestedActions: string;
  internalNotes: string;
  forwardOutOfStock?: string;
  forwardedPharmacy?: {
    name: string;
    address: string;
    postcode: string;
    telephone: string;
    distance: number;
  };
}

interface Prescription {
  id: string;
  prescriptionItems: any[];
  prescriptionCategory?: string;
  status?: string;
  issuedAt?: string;
  printedAt?: string;
  pharmacy?: string;
}

interface EmergencySupplyMedicationItem {
  id: string;
  medication: string;
  supplied: "yes" | "no" | "";
  packBrand?: string;
  quantity?: string;
  daysSupply?: string;
  doseInstructions?: string;
  notes?: string;
  notSuppliedReason?: string;
  notSuppliedReasonOther?: string;
}

interface NHS111EmergencySupplySummaryProps {
  isDarkMode?: boolean;
  data: NHS111SummaryData;
  prescriptions: Prescription[];
  emergencySupplyMedications?: EmergencySupplyMedicationItem[];
  patientId?: string;
}

export function NHS111EmergencySupplySummary({
  isDarkMode = false,
  data,
  prescriptions,
  emergencySupplyMedications = [],
  patientId,
}: NHS111EmergencySupplySummaryProps) {
  const { getHtmlData } = usePrescriptionData();
  const [isDownloading, setIsDownloading] = useState(false);
  const formData_source = getNhs111EmergencySupplyFormData();

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "Not specified";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return "Not specified";
    return timeStr;
  };

  const getOutcomeLabel = (outcome: string) => {
    return (
      formData_source.outcomeLabels[
        outcome as keyof typeof formData_source.outcomeLabels
      ] || outcome
    );
  };

  const downloadGPNotification = () => {
    // Generate GP notification PDF
    const notificationContent = generateGPNotification();
    const blob = new Blob([notificationContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `GP_Notification_${new Date().toISOString().split("T")[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadPrescriptionPDF = async (patientId: string) => {
    try {
      setIsDownloading(true);

      const patientData = await getPatientByPatientId(patientId);

      const Drugs = emergencySupplyMedications.map((item: any) => ({
        PrescriptionItemId: item.id,
        drugName: item.medication,
        quantity: item.quantity ? parseFloat(item.quantity) : 0,
        dose: item.doseInstructions || "",
        note: item.notes || item.notSuppliedReason,
      }));

      const formattedData = {
        patient: patientData,
        patientPrescription: {
          prescriptionCategory: 1,
        },
      };

      const prescriptionHTML = await getHtmlData(formattedData, Drugs, false);

      const link = document.createElement("a");
      link.href = prescriptionHTML.base64Image;
      link.download = "prescription.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const generateGPNotification = () => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>GP Notification - NHS 111 Emergency Supply</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 1.25rem; }
          h1 { color: #005EB8; }
          .section { margin-bottom: 1.25rem; }
          .label { font-weight: bold; }
          .medication-item { padding: 0.625rem; margin: 0.625rem 0; border-left: 3px solid #005EB8; background: #f4f4f4; }
          .supplied { border-left-color: #28a745; }
          .not-supplied { border-left-color: #dc3545; }
        </style>
      </head>
      <body>
        <h1>NHS 111 Emergency Supply Notification</h1>
        <div class="section">
          <p class="label">Date:</p>
          <p>${formatDate(data.consultationDate)} at ${formatTime(data.consultationTime)}</p>
        </div>
        <div class="section">
          <p class="label">Consultation Method:</p>
          <p>${data.consultationMethod}</p>
        </div>
        ${
          emergencySupplyMedications.length > 0
            ? `
        <div class="section">
          <p class="label">Emergency Supply Medications:</p>
          ${emergencySupplyMedications
            .map(
              (med) => `
            <div class="medication-item ${med.supplied === "yes" ? "supplied" : "not-supplied"}">
              <p><strong>${med.medication}</strong></p>
              ${
                med.supplied === "yes"
                  ? `
                <p>Pack/Brand: ${med.packBrand}</p>
                <p>Quantity: ${med.quantity} | Days Supply: ${med.daysSupply}</p>
                <p>Dose: ${med.doseInstructions}</p>
                ${med.notes ? `<p>Notes: ${med.notes}</p>` : ""}
                <p><em>Status: Supplied</em></p>
              `
                  : `
                <p><em>Not Supplied - ${
                  med.notSuppliedReason === "other"
                    ? `Other: ${med.notSuppliedReasonOther}`
                    : formData_source.notSuppliedReasons[
                        med.notSuppliedReason as keyof typeof formData_source.notSuppliedReasons
                      ] || med.notSuppliedReason
                }</em></p>
              `
              }
            </div>
          `,
            )
            .join("")}
        </div>
        `
            : ""
        }
        ${
          data.forwardOutOfStock === "yes" && data.forwardedPharmacy
            ? `
        <div class="section">
          <p class="label">Onward Referral - Forwarded to Pharmacy:</p>
          <p><strong>${data.forwardedPharmacy.name}</strong></p>
          <p>${data.forwardedPharmacy.address}, ${data.forwardedPharmacy.postcode}</p>
          <p>Tel: ${data.forwardedPharmacy.telephone}</p>
          <p>Distance: ${data.forwardedPharmacy.distance} miles</p>
        </div>
        `
            : ""
        }
        <div class="section">
          <p class="label">Outcome:</p>
          <p>${getOutcomeLabel(data.outcomes)}</p>
        </div>
        ${
          data.planAndRequestedActions
            ? `
        <div class="section">
          <p class="label">Plan & Requested Actions:</p>
          <p>${data.planAndRequestedActions}</p>
        </div>
        `
            : ""
        }
      </body>
      </html>
    `;
  };

  const SectionCard = ({
    title,
    children,
  }: {
    title: string;
    children: React.ReactNode;
  }) => (
    <div
      className={`border rounded-lg p-4 ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      }`}
    >
      <h3
        className={`text-sm font-semibold mb-3 ${
          isDarkMode ? "text-white" : "text-gray-900"
        }`}
      >
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </div>
  );

  const DataRow = ({
    label,
    value,
  }: {
    label: string;
    value: string | boolean;
  }) => (
    <div className="flex justify-between text-sm">
      <span className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
        {label}:
      </span>
      <span
        className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
      >
        {typeof value === "boolean"
          ? value
            ? "Yes"
            : "No"
          : value || "Not specified"}
      </span>
    </div>
  );

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div>
        <h2
          className={`text-2xl font-bold mb-2 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          NHS 111 Emergency Supply - Summary
        </h2>
        <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
          Consultation completed on {formatDate(data.consultationDate)} at{" "}
          {formatTime(data.consultationTime)}
        </p>
      </div>

      {/* Quick Actions */}
      <div
        className={`border rounded-lg p-4 ${
          isDarkMode
            ? "border-gray-700 bg-gray-800"
            : "border-gray-200 bg-white"
        }`}
      >
        <h3
          className={`text-sm font-semibold mb-3 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Quick Actions
        </h3>
        <div className="space-y-3">
          {/* GP Notification */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span
                className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                GP Notification has been sent via FHIR
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={downloadGPNotification}
              className={isDarkMode ? "border-gray-600 text-gray-300" : ""}
            >
              <FileDown className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>

          {/* Prescription */}
          {(prescriptions.length > 0 ||
            emergencySupplyMedications.length > 0) && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span
                  className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Prescription has been created
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={isDownloading}
                onClick={() => patientId && downloadPrescriptionPDF(patientId)}
                className={isDarkMode ? "border-gray-600 text-gray-300" : ""}
              >
                <FileDown className="w-4 h-4 mr-2" />
                {isDownloading ? "Please wait..." : "Download PDF"}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Consultation Details */}
      <SectionCard title="Consultation Details">
        <DataRow label="Date" value={formatDate(data.consultationDate)} />
        <DataRow label="Time" value={formatTime(data.consultationTime)} />
        <DataRow label="Method" value={data.consultationMethod} />
        <DataRow
          label="Patient accompanied"
          value={data.patientAccompanied === "yes" ? "Yes" : "No"}
        />
        {data.patientAccompanied === "yes" && (
          <DataRow
            label="Relationship"
            value={
              data.accompaniedRelationship === "other"
                ? data.accompaniedRelationshipOther
                : data.accompaniedRelationship
            }
          />
        )}
      </SectionCard>

      {/* Request Information */}
      <SectionCard title="Request Information">
        <DataRow
          label="Reason for request"
          value={
            data.reasonForRequest === "other"
              ? data.reasonForRequestOther
              : data.reasonForRequest
          }
        />
        <DataRow
          label="NCRS record accessed"
          value={data.ncrsRecordAccessed === "yes" ? "Yes" : "No"}
        />
        <DataRow
          label="Electronic prescription available"
          value={data.electronicPrescriptionAvailable === "yes" ? "Yes" : "No"}
        />
      </SectionCard>

      {/* Outcomes */}
      <SectionCard title="Outcomes">
        <DataRow
          label="Outcome"
          value={
            data.outcomes === "other"
              ? data.outcomesOther
              : getOutcomeLabel(data.outcomes)
          }
        />
      </SectionCard>

      {/* Referral Details */}
      {(data.outcomes === "non-urgent-signposting" ||
        data.outcomes === "urgent-escalation") && (
        <SectionCard title="Referral Details">
          {data.outcomes === "non-urgent-signposting" && (
            <DataRow
              label="Referral destination"
              value={
                data.referralDestination === "other"
                  ? data.referralDestinationOther
                  : data.referralDestination
              }
            />
          )}
          {data.outcomes === "urgent-escalation" && (
            <DataRow
              label="Escalation to"
              value={
                data.escalationDestination === "other"
                  ? data.escalationDestinationOther
                  : data.escalationDestination
              }
            />
          )}
          {data.onwardReferralReason && (
            <DataRow
              label="Referral reason"
              value={data.onwardReferralReason}
            />
          )}
          {data.onwardReferralDate && (
            <DataRow
              label="Referral date"
              value={formatDate(data.onwardReferralDate)}
            />
          )}
          {data.onwardReferralTime && (
            <DataRow
              label="Referral time"
              value={formatTime(data.onwardReferralTime)}
            />
          )}
          {data.destinationOdsCode && (
            <DataRow label="ODS code" value={data.destinationOdsCode} />
          )}
        </SectionCard>
      )}

      {/* Advice and Support */}
      <SectionCard title="Advice and Support">
        <DataRow label="Advice given" value={data.adviceGiven} />
        {data.adviceProvidedTo && (
          <DataRow label="Advice provided to" value={data.adviceProvidedTo} />
        )}
      </SectionCard>

      {/* Emergency Supply Medications */}
      {emergencySupplyMedications.length > 0 && (
        <SectionCard title="Emergency Supply Medications">
          {emergencySupplyMedications.map((medication, index) => (
            <div
              key={medication.id}
              className="mb-3 pb-3 border-b last:border-b-0 last:pb-0 last:mb-0"
            >
              <p
                className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
              >
                {medication.medication}
              </p>
              {medication.supplied === "yes" ? (
                <div className="mt-2 space-y-1">
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    <span className="font-medium">Pack/Brand:</span>{" "}
                    {medication.packBrand}
                  </p>
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    <span className="font-medium">Quantity:</span>{" "}
                    {medication.quantity} •{" "}
                    <span className="font-medium">Days Supply:</span>{" "}
                    {medication.daysSupply}
                  </p>
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    <span className="font-medium">Dose:</span>{" "}
                    {medication.doseInstructions}
                  </p>
                  {medication.notes && (
                    <p
                      className={`text-xs italic ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                    >
                      <span className="font-medium">Notes:</span>{" "}
                      {medication.notes}
                    </p>
                  )}
                  <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 mt-1">
                    Supplied
                  </span>
                </div>
              ) : (
                <div className="mt-2">
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    <span className="font-medium">Not Supplied:</span>{" "}
                    {medication.notSuppliedReason === "other"
                      ? `Other: ${medication.notSuppliedReasonOther}`
                      : formData_source.notSuppliedReasons[
                          medication.notSuppliedReason as keyof typeof formData_source.notSuppliedReasons
                        ] || medication.notSuppliedReason}
                  </p>
                  <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 mt-1">
                    Not Supplied
                  </span>
                </div>
              )}
            </div>
          ))}
        </SectionCard>
      )}

      {/* Forward Out of Stock Medications */}
      {emergencySupplyMedications.some(
        (med) => med.notSuppliedReason === "not-in-stock",
      ) &&
        data.forwardOutOfStock && (
          <SectionCard title="Forward out of stock medication(s)">
            <DataRow
              label="Forward to another pharmacy"
              value={data.forwardOutOfStock === "yes" ? "Yes" : "No"}
            />
            {data.forwardOutOfStock === "yes" && data.forwardedPharmacy && (
              <>
                <DataRow
                  label="Pharmacy Name"
                  value={data.forwardedPharmacy.name}
                />
                <DataRow
                  label="Address"
                  value={`${data.forwardedPharmacy.address}, ${data.forwardedPharmacy.postcode}`}
                />
                <DataRow
                  label="Telephone"
                  value={data.forwardedPharmacy.telephone}
                />
                <DataRow
                  label="Distance"
                  value={`${data.forwardedPharmacy.distance} miles`}
                />
              </>
            )}
          </SectionCard>
        )}

      {/* Prescriptions */}
      {prescriptions.length > 0 && (
        <SectionCard title="Prescriptions">
          {prescriptions.map((prescription, index) => (
            <div key={prescription.id} className="mb-2">
              <p
                className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Prescription #{index + 1}
              </p>
              <p
                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {prescription.prescriptionItems.length} item(s)
              </p>
            </div>
          ))}
        </SectionCard>
      )}

      {/* Notes */}
      {(data.planAndRequestedActions || data.internalNotes) && (
        <SectionCard title="Notes">
          {data.planAndRequestedActions && (
            <div>
              <p
                className={`text-xs font-semibold mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Plan & requested actions:
              </p>
              <p
                className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                {data.planAndRequestedActions}
              </p>
            </div>
          )}
          {data.internalNotes && (
            <div className="mt-3">
              <p
                className={`text-xs font-semibold mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Internal notes:
              </p>
              <p
                className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                {data.internalNotes}
              </p>
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
}
